// Client for the Langfuse Bridge (ui/langfuse-bridge), reached via the
// `/langfuse-bridge` proxy (webpack dev) / rewrite (Vercel). The dashboard never
// talks to Langfuse directly or holds its credentials — the bridge owns that.

import { getToken } from './auth.session';

// Each langfuse-bridge deployment is pinned to one Langfuse environment
// server-side. To make a dev/qa/prod dashboard always reach the matching bridge,
// pick the proxy prefix from REACT_APP_API_ENV — the same build-time switch that
// routes the Compass API (`/api-dev`, `/api-qa`, `/api` in api.config.ts). Each
// prefix is rewritten to the env's bridge subdomain in ui/dashboard/vercel.json
// (prod) and proxied in webpack.config.js (dev). No per-env URL is hardcoded here.
const BRIDGE_PROXY_PATH_MAP: Record<string, string> = {
  dev: '/langfuse-bridge-dev',
  qa: '/langfuse-bridge-qa',
  prod: '/langfuse-bridge',
};

/**
 * Resolve the env-scoped bridge prefix from REACT_APP_API_ENV. Throws (no silent
 * fallback) when it's missing or not one of dev|qa|prod — a misconfigured deploy
 * must fail loudly rather than quietly route to the prod Langfuse project.
 * Resolved per call so the failure surfaces at the QC feature (error toast /
 * inline state), not by crashing the whole app at module load.
 */
function bridgePrefix(): string {
  const env = process.env.REACT_APP_API_ENV;
  const base = env ? BRIDGE_PROXY_PATH_MAP[env] : undefined;
  if (!base) {
    throw new Error(
      `Langfuse bridge misconfigured: REACT_APP_API_ENV must be one of dev|qa|prod (got ${
        env ?? 'undefined'
      })`,
    );
  }
  return `${base}/api/v1`;
}

// ── Wire DTOs (snake_case, mirror the bridge's responses) ──────────

export interface BridgeTraceSummary {
  trace_id: string;
  name: string | null;
  timestamp: string | null;
  environment: string | null;
}

export interface BridgeTraceObservation {
  id: string;
  name: string | null;
  type: string | null;
  parent_observation_id: string | null;
  start_time: string | null;
  input: unknown;
  output: unknown;
}

export interface BridgeTraceDetail extends BridgeTraceSummary {
  input: unknown;
  output: unknown;
  metadata: unknown;
  observations: BridgeTraceObservation[];
}

export interface BridgeDataset {
  id: string;
  name: string;
  description: string | null;
}

export interface BridgeCreatedItem {
  id: string;
  dataset_id: string;
  source_trace_id: string;
}

export interface BridgeTraceDatasetMembership {
  dataset_id: string;
  dataset_name: string;
  item_id: string;
}

export interface BridgeDatasetItemDetail {
  id: string;
  expected_output: unknown;
}

interface BridgeErrorBody {
  error?: string;
  message?: string;
  code?: number;
}

/** Build the error thrown for a non-2xx bridge response (message from body, sanitized). */
function bridgeError(status: number, text: string): Error {
  let message = `Request failed (${status})`;
  try {
    const parsed = text ? (JSON.parse(text) as BridgeErrorBody) : null;
    if (parsed?.message) message = parsed.message;
  } catch {
    // non-JSON error body — keep the generic message (avoid surfacing raw text)
  }
  return new Error(message);
}

// NOTE: bridge reads are intentionally NOT cancellable — no AbortSignal is passed.
// React Query only aborts an in-flight request on observer removal if the queryFn
// consumes `signal`; by not consuming it, switching traces (or closing the modal)
// leaves the previous request running to completion so its result still lands in
// the cache. That means flipping back to an already-fetched trace is instant, and
// requests are never cancelled mid-flight (no spurious AbortErrors).
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getToken();
  const res = await fetch(`${bridgePrefix()}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body != null ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  if (!res.ok) throw bridgeError(res.status, text);

  return text ? (JSON.parse(text) as T) : (null as T);
}

export const langfuseBridgeService = {
  async listTracesByCallId(callId: string): Promise<BridgeTraceSummary[]> {
    const res = await request<{ traces: BridgeTraceSummary[] }>(
      'GET',
      `/traces?call_id=${encodeURIComponent(callId)}`,
    );
    return res?.traces ?? [];
  },

  async getTrace(traceId: string): Promise<BridgeTraceDetail> {
    const res = await request<{ trace: BridgeTraceDetail }>(
      'GET',
      `/traces/${encodeURIComponent(traceId)}`,
    );
    return res.trace;
  },

  async listTraceDatasetMemberships(traceId: string): Promise<BridgeTraceDatasetMembership[]> {
    const res = await request<{ dataset_items: BridgeTraceDatasetMembership[] }>(
      'GET',
      `/traces/${encodeURIComponent(traceId)}/dataset-items`,
    );
    return res?.dataset_items ?? [];
  },

  async listDatasets(): Promise<BridgeDataset[]> {
    const res = await request<{ datasets: BridgeDataset[] }>('GET', '/datasets');
    return res?.datasets ?? [];
  },

  async createDatasetItem(
    datasetId: string,
    payload: { source_trace_id: string; expected_output: unknown },
  ): Promise<BridgeCreatedItem> {
    const res = await request<{ item: BridgeCreatedItem }>(
      'POST',
      `/datasets/${encodeURIComponent(datasetId)}/items`,
      payload,
    );
    return res.item;
  },

  async getDatasetItem(datasetId: string, itemId: string): Promise<BridgeDatasetItemDetail> {
    const res = await request<{ item: BridgeDatasetItemDetail }>(
      'GET',
      `/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}`,
    );
    return res.item;
  },

  async updateDatasetItem(
    datasetId: string,
    itemId: string,
    payload: { source_trace_id: string; expected_output: unknown },
  ): Promise<BridgeCreatedItem> {
    const res = await request<{ item: BridgeCreatedItem }>(
      'POST',
      `/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}`,
      payload,
    );
    return res.item;
  },

  async deleteDatasetItem(datasetId: string, itemId: string): Promise<void> {
    await request<{ deleted: boolean }>(
      'DELETE',
      `/datasets/${encodeURIComponent(datasetId)}/items/${encodeURIComponent(itemId)}`,
    );
  },
};
